import React, { useState } from 'react';

import { Accordion, Box, BoxProps, Button, Center, Group, Image, Loader, Text } from '@mantine/core';
import { IconList } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { YoutubeVideoComponent } from 'components/BatchResourceSelector/YoutubeVideoSelector/YoutubeVideo';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { YoutubeService } from 'services/youtube-service';
import { YoutubePlaylist, YoutubePlaylistVideosResult, YoutubeVideo } from 'shared/interfaces/Youtube';

interface YoutubePlaylistProps extends BoxProps {
  playlist: YoutubePlaylist;
  isVideoSelected: (videoId: string) => boolean;
  onVideoSelect: (youtubeVideo: YoutubeVideo) => void;
}

export function YoutubePlaylistComponent({
  playlist,
  isVideoSelected,
  onVideoSelect,
  ...boxProps
}: YoutubePlaylistProps) {
  const [playlistVideos, setPlaylistVideos] = useState<YoutubeVideo[]>([]);
  const [playlistVideosIds, setPlaylistVideosIds] = useState<Set<string>>(new Set());
  const [lastPlalistVideosResult, setPlaylistChannelVideosResult] = useState<YoutubePlaylistVideosResult>();
  const { t } = useTranslation();

  const playlistVideosMutation = useMutation({
    mutationFn: YoutubeService.getPlaylistVideos,
    onSuccess: (playlistVideosResult: YoutubePlaylistVideosResult) => {
      setPlaylistChannelVideosResult(playlistVideosResult);
      const filteredVideos = playlistVideosResult.videos.filter((video) => !playlistVideosIds.has(video.id));
      setPlaylistVideos([...playlistVideos, ...filteredVideos]);
      setPlaylistVideosIds(new Set([...playlistVideosIds, ...filteredVideos.map((video) => video.id)]));
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.YOUTUBE.PLAYLIST_VIDEOS.FAILURE')
  });

  const loadMorePlaylistVideos = () => {
    if (!lastPlalistVideosResult?.nextPageToken) return;
    playlistVideosMutation.mutate({
      playlistId: playlist.id,
      nextPageToken: lastPlalistVideosResult.nextPageToken
    });
  };

  const loadFirstPlaylistVideos = (value: string | null) => {
    if (!value || playlistVideos.length > 0) return;
    playlistVideosMutation.mutate({
      playlistId: playlist.id
    });
  };
  return (
    <Accordion
      chevronPosition="right"
      variant="contained"
      mt={10}
      mb={10}
      onChange={(value) => loadFirstPlaylistVideos(value)}
      {...boxProps}>
      <Accordion.Item value={playlist.id}>
        <Accordion.Control>
          <Group noWrap>
            <Box w={100} pos="relative">
              <Image width={100} fit="cover" src={playlist.imageUrl} withPlaceholder />
              <Box
                pos="absolute"
                top={0}
                left={0}
                w="100%"
                h="100%"
                sx={{ backgroundColor: 'black', opacity: 0.5, borderRadius: '15px' }}></Box>
              <Box pos="absolute" top="30%" left="30%">
                <IconList color="white" size={35} />
              </Box>
            </Box>

            <Box>
              <Text lineClamp={1}>{playlist.title}</Text>
              <Text lineClamp={2}>{playlist.description}</Text>
            </Box>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {playlistVideos.map((video, key: number) => {
            return (
              <YoutubeVideoComponent
                key={key}
                video={video}
                onVideoSelect={onVideoSelect}
                isSelected={isVideoSelected(video.id)}
              />
            );
          })}
          {playlistVideos.length === 0 && playlistVideosMutation.isLoading && !playlistVideosMutation.isError && (
            <Center mt={10} mb={10} h={100}>
              <Loader variant="dots" />
            </Center>
          )}
          {playlistVideos.length !== 0 && lastPlalistVideosResult?.nextPageToken && (
            <Center mt={10} mb={10}>
              <Button loading={playlistVideosMutation.isLoading} onClick={() => loadMorePlaylistVideos()}>
                {t('APP.ACTIONS.LOAD_MORE')}
              </Button>
            </Center>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
